import { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import { modalProps } from "../../../../Component/Interface/Modal";
import { RowView } from "../../../../Component/style/common_style";
import url from "../../../../Component/server";

interface LoginModalProps extends modalProps {
  setTryLogin?: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setBlockModal?: React.Dispatch<React.SetStateAction<boolean>>;
  returnTo?: string;
}

const LoginModal = ({
  visible,
  handleModal,
  returnTo,
}: LoginModalProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const redirecting = useRef(false);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!visible) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    redirecting.current = false;
    setPending(false);
    cancelRef.current?.focus();
    return () => { previousFocus?.focus(); };
  }, [visible]);
  const closeModal = () => {
    handleModal();
  };
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const goKaKaoLogin = () => {
    if (redirecting.current) return;
    redirecting.current = true;
    setPending(true);
    const requestedPath = returnTo || window.location.pathname + window.location.search + window.location.hash;
    const nextPath = requestedPath.startsWith("/") && !requestedPath.startsWith("//") && !requestedPath.includes("\\") ? requestedPath : "/";
    try { sessionStorage.setItem("authReturnTo", nextPath); } catch { /* nextPath is also carried by the login request. */ }
    const loginUrl = new URL(url.Communication_url.kakao_login, window.location.origin);
    loginUrl.searchParams.set('nextPath', nextPath);
    window.location.assign(loginUrl.href);
  };

  if (!visible) return null;

  return (
    <Container onClick={closeModal}>
      <ModalBox ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={stopPropagation}
        onKeyDown={event => {
          if (event.key === "Escape") { event.preventDefault(); closeModal(); }
          if (event.key === "Tab") {
            const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
            if (!buttons?.length) return;
            const first = buttons[0], last = buttons[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          }
        }}>
        <Title id={titleId}>로그인 안내</Title>
        <ContentDiv>
          <div>로그인이 필요한 서비스입니다.</div>
        </ContentDiv>
        <BtnDiv>
          <ApplyBtn type="button" disabled={pending} onClick={goKaKaoLogin} aria-label={pending ? "카카오 로그인 처리 중" : "카카오 로그인"}>
            <img src="/kakao-login-ko.svg" alt="" width="224" height="46" />
          </ApplyBtn>
          <CancelBtn ref={cancelRef} type="button" onClick={closeModal}>취소</CancelBtn>
        </BtnDiv>
      </ModalBox>
    </Container>
  );
};
export default LoginModal;

const Container = styled.div`
  z-index: 10000;
  background-color: #00000085;
  position: fixed;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  padding: 16px;
`;

const ModalBox = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 25rem;
  margin: auto 0rem;
  background-color: var(--user-surface);
  color: var(--user-text);
  border-radius: 8px;
  overflow: hidden;
  padding: 28px 24px;
  div.info {
    color: var(--user-text-muted);
    font-size: 14px;
    text-align: center;
    line-height: 19px;
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 20px;
  font-family: var(--font-Pretendard-SemiBold);
`;

const ContentDiv = styled.div`
  padding: 20px 0px;
  display: flex;
  font-size: 16px;
  font-family: var(--font-Pretendard-Regular);
  flex-direction: column;
  color: var(--user-text);
`;

const BtnDiv = styled(RowView)`
  position: relative;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
`;

const Btn = styled.button`
  border: 0;
  &:focus-visible { outline: 2px solid #7955f0; outline-offset: 2px; }
  &:disabled { opacity: 0.6; cursor: wait; }
  border-radius: 8px;
  padding: 14px 0px;
  flex: 1;
  align-items: center;
  justify-content: center;
  display: flex;
  font-size: 16px;
  font-family: var(--font-Pretendard-SemiBold);
  cursor: pointer;
`;

const CancelBtn = styled(Btn)`
  background-color: var(--user-surface-raised);
  border: 1px solid var(--user-border);
  color: var(--user-text);
`;

const ApplyBtn = styled(Btn)`
  min-height: 45px;
  padding: 0;
  overflow: hidden;
  border-radius: 12px;
  background-color: #fee500;
  img { display: block; width: 100%; height: 45px; object-fit: contain; }
`;

